import { useLocation } from "react-router-dom";
import { Link, NavLink } from "../lib/route";
import { useEffect, useState } from "react";
import { Menu, X, Phone, Flame } from "lucide-react";
import { SITE, HEADER } from "../lib/_data";

const DEFAULT_NAV = [
  { link: "/", label: "Home" },
  { link: "/services", label: "Services" },
  { link: "/about", label: "About" },
  { link: "/reviews", label: "Reviews" },
  { link: "/faq", label: "FAQ" },
  { link: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const nav = (Array.isArray(HEADER.nav_items) && HEADER.nav_items.length > 0)
    ? HEADER.nav_items.map((n) => ({ link: n.link || "/", label: n.label || "" }))
    : DEFAULT_NAV;
  const logoText = HEADER.logo_text || SITE.brand;
  const logoImage = HEADER.logo_image_url || "";
  const showPhone = HEADER.show_phone !== false;
  const ctaText = HEADER.cta_text || "Book Consultation";
  const ctaLink = HEADER.cta_link || "/consultations";
  const ctaNewTab = !!HEADER.cta_open_new_tab;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#f9f6f0]/95 backdrop-blur-xl border-b border-[#e5e0d8]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center gap-2 group"
        >
          {logoImage ? (
            <img
              src={logoImage}
              alt={logoText || "Logo"}
              className="h-10 w-10 rounded-full object-cover shadow-[0_6px_20px_rgba(200,90,60,0.35)]"
              data-testid="header-logo-image"
            />
          ) : (
            <span className="w-10 h-10 rounded-full bg-[#c85a3c] text-[#fffdf9] flex items-center justify-center shadow-[0_6px_20px_rgba(200,90,60,0.35)] group-hover:scale-105 transition-transform">
              <Flame className="w-5 h-5" strokeWidth={2.4} />
            </span>
          )}
          <div className="leading-tight">
            <div className="font-display text-xl font-bold text-[#2c2a29]" data-testid="header-logo-text">
              {logoText}
            </div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#8b5d4e] font-semibold -mt-0.5">
              {SITE.brandTagline || "Local Home Services"}
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {nav.map((n, i) => (
            <NavLink
              key={`${n.label}-${i}`}
              to={n.link}
              data-testid={`nav-${(n.label || "").toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  isActive
                    ? "bg-[#2c2a29] text-[#fffdf9]"
                    : "text-[#2c2a29] hover:bg-[#e5e0d8]"
                }`
              }
              end={n.link === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {showPhone && SITE.phone && (
            <a
              href={SITE.phoneHref}
              data-testid="header-call-link"
              className="hidden md:flex items-center gap-2 text-sm font-semibold text-[#2c2a29] hover:text-[#c85a3c] transition-colors"
            >
              <Phone className="w-4 h-4" strokeWidth={2.4} />
              {SITE.phone}
            </a>
          )}
          {ctaText && (
            <Link
              to={ctaLink}
              target={ctaNewTab ? "_blank" : undefined}
              rel={ctaNewTab ? "noreferrer" : undefined}
              data-testid="header-book-cta"
              className="hidden md:inline-flex items-center gap-2 bg-[#c85a3c] hover:bg-[#a64b32] text-[#fffdf9] font-bold text-sm px-5 py-3 rounded-full shadow-[0_8px_24px_rgba(200,90,60,0.35)] transition-all hover:-translate-y-0.5"
            >
              {ctaText}
            </Link>
          )}
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-11 h-11 rounded-full bg-[#2c2a29] text-[#fffdf9] flex items-center justify-center"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#f9f6f0] border-t border-[#e5e0d8] px-6 py-6 space-y-1"
        >
          {nav.map((n, i) => (
            <NavLink
              key={`${n.label}-m-${i}`}
              to={n.link}
              data-testid={`mobile-nav-${(n.label || "").toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `block px-4 py-3 rounded-lg text-base font-semibold ${
                  isActive
                    ? "bg-[#2c2a29] text-[#fffdf9]"
                    : "text-[#2c2a29] hover:bg-[#e5e0d8]"
                }`
              }
              end={n.link === "/"}
            >
              {n.label}
            </NavLink>
          ))}
          <div className="pt-4 flex flex-col gap-2">
            {showPhone && SITE.phone && (
              <a
                href={SITE.phoneHref}
                data-testid="mobile-call-link"
                className="flex items-center justify-center gap-2 border-2 border-[#2c2a29] text-[#2c2a29] py-3 rounded-full font-bold"
              >
                <Phone className="w-4 h-4" />
                {SITE.phone}
              </a>
            )}
            {ctaText && (
              <Link
                to={ctaLink}
                target={ctaNewTab ? "_blank" : undefined}
                rel={ctaNewTab ? "noreferrer" : undefined}
                data-testid="mobile-book-cta"
                className="text-center bg-[#c85a3c] text-[#fffdf9] py-3 rounded-full font-bold"
              >
                {ctaText}
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
